/**
 * 性能优化器
 * 提供各种性能优化策略和工具
 */

import { EventEmitter } from 'events';
import * as LRU from 'lru-cache';
import { logger } from '../utils/logger';
import pLimit from 'p-limit';
import { debounce, throttle } from 'lodash';

/**
 * 缓存配置
 */
export interface CacheConfig {
  max?: number;           // 最大缓存数
  maxAge?: number;        // 最大缓存时间（毫秒）
  updateAgeOnGet?: boolean; // 获取时更新时间
}

/**
 * 批处理配置
 */
export interface BatchConfig {
  maxSize: number;        // 最大批次大小
  maxWait: number;        // 最大等待时间
  processInterval?: number; // 处理间隔
}

/**
 * 性能优化器
 */
export class PerformanceOptimizer extends EventEmitter {
  private caches: Map<string, LRU<string, any>> = new Map();
  private batches: Map<string, BatchProcessor<any>> = new Map();
  private limiters: Map<string, any> = new Map();
  
  /**
   * 创建缓存
   */
  createCache<T>(name: string, config: CacheConfig = {}): LRU<string, T> {
    const cache = new LRU<string, T>({
      max: config.max || 500,
      maxAge: config.maxAge || 1000 * 60 * 5, // 默认5分钟
      updateAgeOnGet: config.updateAgeOnGet !== false
    });
    
    this.caches.set(name, cache);
    logger.debug(`Created cache: ${name}`);
    
    return cache;
  }
  
  /**
   * 获取缓存
   */
  getCache<T>(name: string): LRU<string, T> | undefined {
    return this.caches.get(name);
  }
  
  /**
   * 缓存装饰器
   */
  memoize<T extends (...args: any[]) => any>(
    fn: T,
    options: {
      key?: (...args: Parameters<T>) => string;
      cache?: string;
      ttl?: number;
    } = {}
  ): T {
    const cacheName = options.cache || 'default';
    let cache = this.getCache<ReturnType<T>>(cacheName);
    
    if (!cache) {
      cache = this.createCache<ReturnType<T>>(cacheName, {
        maxAge: options.ttl
      });
    }
    
    const keyFn = options.key || ((...args: any[]) => JSON.stringify(args));
    
    return ((...args: Parameters<T>) => {
      const key = keyFn(...args);
      const cached = cache!.get(key);
      
      if (cached !== undefined) {
        logger.debug(`Cache hit: ${cacheName}/${key}`);
        return cached;
      }
      
      const result = fn(...args);
      cache!.set(key, result);
      logger.debug(`Cache miss: ${cacheName}/${key}`);
      
      return result;
    }) as T;
  }
  
  /**
   * 异步缓存装饰器
   */
  memoizeAsync<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    options: {
      key?: (...args: Parameters<T>) => string;
      cache?: string;
      ttl?: number;
    } = {}
  ): T {
    const cacheName = options.cache || 'default-async';
    let cache = this.getCache<Promise<Awaited<ReturnType<T>>>>(cacheName);
    
    if (!cache) {
      cache = this.createCache<Promise<Awaited<ReturnType<T>>>>(cacheName, {
        maxAge: options.ttl
      });
    }
    
    const keyFn = options.key || ((...args: any[]) => JSON.stringify(args));
    
    return (async (...args: Parameters<T>) => {
      const key = keyFn(...args);
      const cached = cache!.get(key);
      
      if (cached !== undefined) {
        logger.debug(`Async cache hit: ${cacheName}/${key}`);
        return cached;
      }
      
      // 缓存 Promise 以防止并发请求
      const promise = fn(...args);
      cache!.set(key, promise);
      
      try {
        const result = await promise;
        return result;
      } catch (error) {
        // 错误时清除缓存
        cache!.del(key);
        throw error;
      }
    }) as T;
  }
  
  /**
   * 创建批处理器
   */
  createBatchProcessor<T>(
    name: string,
    processor: (items: T[]) => Promise<void>,
    config: BatchConfig
  ): BatchProcessor<T> {
    const batch = new BatchProcessor(processor, config);
    this.batches.set(name, batch);
    logger.debug(`Created batch processor: ${name}`);
    return batch;
  }
  
  /**
   * 获取批处理器
   */
  getBatchProcessor<T>(name: string): BatchProcessor<T> | undefined {
    return this.batches.get(name);
  }
  
  /**
   * 创建并发限制器
   */
  createLimiter(name: string, concurrency: number): any {
    const limiter = pLimit(concurrency);
    this.limiters.set(name, limiter);
    logger.debug(`Created limiter: ${name} (concurrency: ${concurrency})`);
    return limiter;
  }
  
  /**
   * 获取限制器
   */
  getLimiter(name: string): any {
    return this.limiters.get(name);
  }
  
  /**
   * 防抖优化
   */
  debounce<T extends (...args: any[]) => any>(
    fn: T,
    wait: number,
    options?: {
      leading?: boolean;
      trailing?: boolean;
      maxWait?: number;
    }
  ): T & { cancel: () => void; flush: () => void } {
    return debounce(fn, wait, options);
  }
  
  /**
   * 节流优化
   */
  throttle<T extends (...args: any[]) => any>(
    fn: T,
    wait: number,
    options?: {
      leading?: boolean;
      trailing?: boolean;
    }
  ): T & { cancel: () => void; flush: () => void } {
    return throttle(fn, wait, options);
  }
  
  /**
   * 懒加载
   */
  lazy<T>(factory: () => T): () => T {
    let instance: T | undefined;
    let initialized = false;
    
    return () => {
      if (!initialized) {
        instance = factory();
        initialized = true;
      }
      return instance!;
    };
  }
  
  /**
   * 异步懒加载
   */
  lazyAsync<T>(factory: () => Promise<T>): () => Promise<T> {
    let instance: Promise<T> | undefined;
    
    return () => {
      if (!instance) {
        instance = factory();
      }
      return instance;
    };
  }
  
  /**
   * 分块处理
   */
  async processInChunks<T, R>(
    items: T[],
    processor: (chunk: T[]) => Promise<R[]>,
    chunkSize: number = 100
  ): Promise<R[]> {
    const results: R[] = [];
    
    for (let i = 0; i < items.length; i += chunkSize) {
      const chunk = items.slice(i, i + chunkSize);
      const chunkResults = await processor(chunk);
      results.push(...chunkResults);
      
      // 让出事件循环
      await new Promise(resolve => setImmediate(resolve));
    }
    
    return results;
  }
  
  /**
   * 并行分块处理
   */
  async processInParallelChunks<T, R>(
    items: T[],
    processor: (chunk: T[]) => Promise<R[]>,
    options: {
      chunkSize?: number;
      concurrency?: number;
    } = {}
  ): Promise<R[]> {
    const { chunkSize = 100, concurrency = 3 } = options;
    const chunks: T[][] = [];
    
    // 分块
    for (let i = 0; i < items.length; i += chunkSize) {
      chunks.push(items.slice(i, i + chunkSize));
    }
    
    // 并行处理
    const limiter = pLimit(concurrency);
    const promises = chunks.map(chunk =>
      limiter(() => processor(chunk))
    );
    
    const results = await Promise.all(promises);
    return results.flat();
  }
  
  /**
   * 对象池
   */
  createObjectPool<T>(
    factory: () => T,
    reset: (obj: T) => void,
    maxSize: number = 10
  ): ObjectPool<T> {
    return new ObjectPool(factory, reset, maxSize);
  }
  
  /**
   * 清理资源
   */
  cleanup(): void {
    // 清理缓存
    this.caches.forEach(cache => cache.reset());
    this.caches.clear();
    
    // 清理批处理器
    this.batches.forEach(batch => batch.flush());
    this.batches.clear();
    
    // 清理限制器
    this.limiters.clear();
    
    logger.debug('Performance optimizer cleaned up');
  }
}

/**
 * 批处理器
 */
export class BatchProcessor<T> extends EventEmitter {
  private queue: T[] = [];
  private timer: NodeJS.Timeout | null = null;
  private processing = false;
  
  constructor(
    private processor: (items: T[]) => Promise<void>,
    private config: BatchConfig
  ) {
    super();
  }
  
  /**
   * 添加项目
   */
  add(item: T): void {
    this.queue.push(item);
    
    if (this.queue.length >= this.config.maxSize) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), this.config.maxWait);
    }
  }
  
  /**
   * 批量添加
   */
  addMany(items: T[]): void {
    items.forEach(item => this.add(item));
  }
  
  /**
   * 立即处理
   */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    
    if (this.queue.length === 0 || this.processing) {
      return;
    }
    
    this.processing = true;
    const items = this.queue.splice(0, this.config.maxSize);
    
    try {
      await this.processor(items);
      this.emit('batch:processed', items);
    } catch (error) {
      this.emit('batch:error', error, items);
      logger.error('Batch processing error:', error);
    } finally {
      this.processing = false;
      
      // 如果还有剩余项目，继续处理
      if (this.queue.length > 0) {
        if (this.config.processInterval) {
          setTimeout(() => this.flush(), this.config.processInterval);
        } else {
          setImmediate(() => this.flush());
        }
      }
    }
  }
  
  /**
   * 获取队列大小
   */
  get size(): number {
    return this.queue.length;
  }
}

/**
 * 对象池
 */
export class ObjectPool<T> {
  private pool: T[] = [];
  
  constructor(
    private factory: () => T,
    private reset: (obj: T) => void,
    private maxSize: number
  ) {}
  
  /**
   * 获取对象
   */
  acquire(): T {
    if (this.pool.length > 0) {
      return this.pool.pop()!;
    }
    return this.factory();
  }
  
  /**
   * 释放对象
   */
  release(obj: T): void {
    if (this.pool.length < this.maxSize) {
      this.reset(obj);
      this.pool.push(obj);
    }
  }
  
  /**
   * 清空池
   */
  clear(): void {
    this.pool = [];
  }
  
  /**
   * 获取池大小
   */
  get size(): number {
    return this.pool.length;
  }
}

/**
 * 全局性能优化器实例
 */
export const performanceOptimizer = new PerformanceOptimizer();

/**
 * 性能优化装饰器
 */
export const cache = performanceOptimizer.memoize.bind(performanceOptimizer);
export const cacheAsync = performanceOptimizer.memoizeAsync.bind(performanceOptimizer);
export const debounceMethod = performanceOptimizer.debounce.bind(performanceOptimizer);
export const throttleMethod = performanceOptimizer.throttle.bind(performanceOptimizer);